
  if (!customElements.get("progress-bar")) {
    customElements.define(
      "progress-bar",
      class ProgressBar extends HTMLElement {
        connectedCallback() {
          this.steps = this.querySelectorAll(".cart-drawer-progress-bar-advanced");
          this.wrapper = this.querySelector(".progress-bar__wrapper");
          this.dragEl = this.querySelector(".progress-bar__bar--drag");
          this.target = +this.dataset.target;
          this.cartTotal = +this.dataset.cartTotal || 0;
          this.allUnlockedMessage = this.dataset.allUnlockedMessage || "";
          this.titleEl = this.querySelector(".progress-bar__title");

          this.HORIZONTAL_BUFFER = 8;
          this._lastCartTotal = this.cartTotal;
          this._isFirstRun = true;

          if (!this.steps.length) return;

          this.distributeAndFill({ animate: false });
          this.updateTitle();
          this.applyMaxHeight();

          requestAnimationFrame(() => {
            if (this.dragEl) this.dragEl.classList.remove("is-no-transition");
            this._isFirstRun = false;
          });

          this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
          if (this.wrapper) this.resizeObserver.observe(this.wrapper);
          this.steps.forEach((step) => this.resizeObserver.observe(step));

          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.scheduleLayout());
          }

          this.querySelectorAll(".cart-drawer-progress-bar-advanced img").forEach((img) => {
            if (!img.complete) {
              img.addEventListener("load", () => this.scheduleLayout(), { once: true });
            }
          });
        }

        disconnectedCallback() {
          if (this.resizeObserver) this.resizeObserver.disconnect();
          if (this._rafId) cancelAnimationFrame(this._rafId);
        }

        morphedCallback() {
          this.steps = this.querySelectorAll(".cart-drawer-progress-bar-advanced");
          this.wrapper = this.querySelector(".progress-bar__wrapper");
          this.dragEl = this.querySelector(".progress-bar__bar--drag");
          this.titleEl = this.querySelector(".progress-bar__title");
          this.target = +this.dataset.target;
          this.allUnlockedMessage = this.dataset.allUnlockedMessage || "";

          if (!this.steps.length) return;

          if (this.resizeObserver) {
            this.steps.forEach((step) => this.resizeObserver.observe(step));
          }

          this.updateCart(this.dataset.cartTotal);
          this.applyMaxHeight();
        }

        scheduleLayout() {
          if (this._rafId) cancelAnimationFrame(this._rafId);
          this._rafId = requestAnimationFrame(() => {
            this.distributeAndFill({ animate: false });
            this.applyMaxHeight();
          });
        }

        updateCart(newTotal) {
          this.cartTotal = +newTotal || 0;
          this.dataset.cartTotal = this.cartTotal;
          const animate = this.cartTotal !== this._lastCartTotal;
          this._lastCartTotal = this.cartTotal;
          this.distributeAndFill({ animate });
          this.updateTitle();
        }

        setBarWidth(pct, animate) {
          if (!this.dragEl) return;
          const clamped = Math.max(0, Math.min(100, pct));

          if (!animate) {
            this.dragEl.classList.add("is-no-transition");
            this.dragEl.style.width = `${clamped}%`;
            this.dragEl.offsetWidth;
            if (!this._isFirstRun) {
              this.dragEl.classList.remove("is-no-transition");
            }
          } else {
            this.dragEl.style.width = `${clamped}%`;
          }
        }

        distributeAndFill({ animate = false } = {}) {
          if (!this.steps.length || !this.wrapper) return;

          const wrapperWidth = this.wrapper.getBoundingClientRect().width;
          if (wrapperWidth <= 0) return;

          const targetCents = this.target * 100;
          if (targetCents <= 0) return;

          Array.from(this.steps).forEach((step) => {
            step.style.maxWidth = "";
          });

          const stepData = Array.from(this.steps)
            .map((el) => {
              const dataEl = el.querySelector(".progress-bar__step-data");
              const amount = dataEl ? +dataEl.dataset.amount : 0;
              return { el, amount, width: el.offsetWidth };
            })
            .filter((s) => s.amount > 0)
            .sort((a, b) => a.amount - b.amount);

          if (!stepData.length) return;

          const positions = stepData.map((s) =>
            Math.min((s.amount / targetCents) * wrapperWidth, wrapperWidth)
          );

          for (let i = 1; i < positions.length; i++) {
            const minPos =
              positions[i - 1] +
              (stepData[i - 1].width + stepData[i].width) / 2 +
              this.HORIZONTAL_BUFFER;
            if (positions[i] < minPos) positions[i] = minPos;
          }

          const lastIdx = positions.length - 1;
          const rightEdge = wrapperWidth - stepData[lastIdx].width / 2;
          if (positions[lastIdx] > rightEdge) {
            positions[lastIdx] = rightEdge;
            for (let i = lastIdx - 1; i >= 0; i--) {
              const maxAllowed =
                positions[i + 1] -
                (stepData[i].width + stepData[i + 1].width) / 2 -
                this.HORIZONTAL_BUFFER;
              if (positions[i] > maxAllowed) positions[i] = maxAllowed;
            }
          }

          if (positions[0] < stepData[0].width / 2) {
            positions[0] = stepData[0].width / 2;
            for (let i = 1; i < positions.length; i++) {
              const minPos =
                positions[i - 1] +
                (stepData[i - 1].width + stepData[i].width) / 2 +
                this.HORIZONTAL_BUFFER;
              if (positions[i] < minPos) positions[i] = minPos;
            }
          }

          stepData.forEach((s, i) => {
            const pct = (positions[i] / wrapperWidth) * 100;
            s.el.style.left = `${pct}%`;
            s.visualPct = pct;
          });

          this.updateBarFill(stepData, targetCents, animate);
        }

        updateBarFill(stepData, targetCents, animate) {
          if (!this.dragEl) return;

          const lastAmount = stepData[stepData.length - 1].amount;

          if (this.cartTotal >= lastAmount) {
            this.setBarWidth(100, animate);
            return;
          }

          const points = [{ amount: 0, pct: 0 }];
          stepData.forEach((s) => points.push({ amount: s.amount, pct: s.visualPct }));
          if (lastAmount < targetCents) {
            points.push({ amount: targetCents, pct: 100 });
          }

          let pct = 0;
          for (let i = 0; i < points.length - 1; i++) {
            if (this.cartTotal >= points[i].amount && this.cartTotal <= points[i + 1].amount) {
              const span = points[i + 1].amount - points[i].amount;
              const t = span > 0 ? (this.cartTotal - points[i].amount) / span : 0;
              pct = points[i].pct + t * (points[i + 1].pct - points[i].pct);
              break;
            }
          }

          this.setBarWidth(pct, animate);
        }

        updateTitle() {
          if (!this.titleEl) return;

          const stepsData = Array.from(this.steps)
            .map((step) => {
              const data = step.querySelector(".progress-bar__step-data");
              if (!data) return null;
              return {
                amount: +(data.dataset.amount || 0),
                remaining: data.dataset.remaining || ""
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.amount - b.amount);

          let nextStep = null;
          stepsData.forEach((step) => {
            if (this.cartTotal < step.amount && !nextStep) {
              nextStep = step;
            }
          });

          if (nextStep) {
            this.titleEl.innerHTML = nextStep.remaining;
          } else if (stepsData.length > 0) {
            this.titleEl.innerHTML = this.allUnlockedMessage;
          }
        }

        applyMaxHeight() {
          if (!this.wrapper || !this.steps.length) return;

          const stepOffset = this.steps[0].offsetTop;
          const maxStepHeight = Math.max(
            ...Array.from(this.steps).map((el) => el.offsetHeight)
          );

          this.wrapper.style.height = `${stepOffset + maxStepHeight}px`;
        }
      }
    );
  }

    (function () {
      if (window.customElements.get('upsells-slider')) return;

      const SIDE_PANELS = (window.breeqUpsellsSidePanels = window.breeqUpsellsSidePanels || {});

      class UpsellsSlider extends HTMLElement {
        constructor() {
          super();
          this.swiper = null;
          this.mediaQuery = null;
          this.onBreakpointChange = null;
          this.pendingFrame = null;
          this.home = null;
        }

        connectedCallback() {
          this.captureHome();

          if (!this.mediaQuery) {
            this.mediaQuery = window.matchMedia('(min-width: 750px)');
            this.onBreakpointChange = () => this.schedule();
            if (this.mediaQuery.addEventListener) {
              this.mediaQuery.addEventListener('change', this.onBreakpointChange);
            } else {
              this.mediaQuery.addListener(this.onBreakpointChange);
            }
          }

          this.schedule();
        }

        disconnectedCallback() {
          this.destroySwiper();
          if (this.pendingFrame) {
            cancelAnimationFrame(this.pendingFrame);
            this.pendingFrame = null;
          }
        }

        morphedCallback() {
          this.schedule();
        }

        schedule() {
          if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
          this.pendingFrame = requestAnimationFrame(() => {
            this.pendingFrame = null;
            if (!this.isConnected) return;
            this.placeSide();
            window.breeqOnSwiperReady(() => {
              if (this.isConnected) this.initSwiper();
            });
          });
        }

        blockWrapper() {
          return this.closest('[id^="shopify-block-"]') || this;
        }

        captureHome() {
          if (this.home) return;
          const wrapper = this.blockWrapper();
          if (wrapper.parentElement) {
            this.home = { parent: wrapper.parentElement, next: wrapper.nextSibling };
          }
        }

        isDesktop() {
          return this.mediaQuery ? this.mediaQuery.matches : false;
        }

        activeLayout() {
          const value = this.isDesktop()
            ? this.dataset.layoutDesktop
            : this.dataset.layoutMobile;
          return value || 'carousel';
        }

        placeSide() {
          const wrapper = this.blockWrapper();

          const drawer = document.getElementById('CartDrawer');
          const host = drawer ? drawer.parentElement : null;
          const blockId = this.dataset.blockId || '';
          const empty = this.dataset.empty === 'true';
          const wanted = this.isDesktop() && this.dataset.layoutDesktop === 'side' && !empty && !!host;

          if (!wanted) {
            const stale = SIDE_PANELS[blockId];
            if (stale && stale !== wrapper) {
              if (stale.isConnected) stale.remove();
              delete SIDE_PANELS[blockId];
            }

            if (wrapper.dataset.side !== 'true') return;

            delete wrapper.dataset.side;
            if (SIDE_PANELS[blockId] === wrapper) delete SIDE_PANELS[blockId];

            const home = this.home;
            if (home && home.parent && home.parent.isConnected) {
              const before = home.next && home.next.parentNode === home.parent ? home.next : null;
              home.parent.insertBefore(wrapper, before);
            }
            return;
          }

          const previous = SIDE_PANELS[blockId];
          if (previous && previous !== wrapper && previous.isConnected) previous.remove();
          SIDE_PANELS[blockId] = wrapper;

          wrapper.dataset.side = 'true';
          if (host.firstElementChild !== wrapper) {
            host.insertBefore(wrapper, host.firstChild);
          }
        }

        initSwiper() {
          if (!this.isConnected) return;
          this.destroySwiper();

          if (this.activeLayout() !== 'carousel') return;

          const root = this.querySelector('[data-slider-root]');
          const prev = this.querySelector('[data-slider-prev]');
          const next = this.querySelector('[data-slider-next]');
          const paginationEl = this.querySelector('[data-slider-pagination]');

          if (!root || !root.querySelector('.swiper-wrapper') || typeof Swiper === 'undefined') return;

          const slidesPerView = parseFloat(this.dataset.slidesPerView) || 2;
          const slidesPerViewDesktop =
            parseFloat(this.dataset.slidesPerViewDesktop) || slidesPerView;
          const rawSpace = parseInt(this.dataset.spaceBetween, 10);
          const spaceBetween = Number.isFinite(rawSpace) ? rawSpace : 10;
          const showNav = this.dataset.navigation === 'true';
          const showPag = this.dataset.pagination === 'true';

          this.swiper = new Swiper(root, {
            slidesPerView: slidesPerView,
            spaceBetween: spaceBetween,
            breakpoints: {
              750: { slidesPerView: slidesPerViewDesktop },
            },
            navigation: showNav ? {
              prevEl: prev,
              nextEl: next
            } : false,
            pagination: showPag ? {
              el: paginationEl,
              clickable: true
            } : false,
            observer: true,
            observeParents: true,
            watchOverflow: true,

            lazyPreloadPrevNext: 2,

            a11y: {
              prevSlideMessage: 'Produit précédent',
              nextSlideMessage: 'Produit suivant',
              paginationBulletMessage: 'Aller au produit {{index}}',
              slideLabelMessage: '{{index}} sur {{slidesLength}}'
            }
          });
        }

        destroySwiper() {
          if (this.swiper && typeof this.swiper.destroy === 'function') {
            try { this.swiper.destroy(true, true); } catch (e) {}
          }
          this.swiper = null;
        }
      }

      window.customElements.define('upsells-slider', UpsellsSlider);
    })();
  
